"use client";

import { useState } from "react";
import type { Card as CardT, Download } from "@/payload-types";
import { Card, CardHeader } from "../ui/card";
import { Dialog, DialogHeader, DialogContent, DialogCloseButton } from "@/components/frontend/ui/dialog";
import { Form } from "@/components/frontend/ui/form/form";
import { DownloadIcon, FileIcon } from "lucide-react";
import { getRelationshipObject } from "@/lib/utils/theme-configuration-class";

interface DownloadItemProps {
	download: Download;
	cardVariant?: CardT;
}

export const DownloadItem: React.FC<DownloadItemProps> = ({ download, cardVariant }) => {
	const [open, setOpen] = useState(false);
	const form = getRelationshipObject(download.protection?.form);

	const card = (
		<Card variant={cardVariant} className="ms-card-with-hover cursor-pointer">
			<CardHeader className="w-full">
				<div className="flex w-full items-center justify-between gap-4">
					<span className="flex min-w-0 items-center gap-3">
						<FileIcon className="h-5 w-5 shrink-0" />
						<span className="truncate">{download.title}</span>
					</span>

					<DownloadIcon className="h-5 w-5 shrink-0" />
				</div>

				{download.description && <p className="mt-1 text-sm text-muted-foreground">{download.description}</p>}
			</CardHeader>
		</Card>
	);

	if (form) {
		return (
			<Dialog
				open={open}
				onOpenChange={setOpen}
				title={download.title}
				variant={download.protection?.settings?.cardVariant ?? cardVariant}
				trigger={
					<button type="button" onClick={() => setOpen(true)} className="block w-full text-left" title={`Download ${download.title}`}>
						{card}
					</button>
				}
			>
				<DialogHeader>
					<div className="flex items-center justify-between gap-3">
						<div className="flex flex-col gap-4">
							<span className="flex min-w-0 items-center gap-3 h3">
								<FileIcon className="h-5 w-5 shrink-0" />
								<span className="truncate">{download.title}</span>
							</span>
							{download.protection?.description && <p className="text-muted-foreground">{download.protection.description}</p>}
						</div>

						<DialogCloseButton />
					</div>
				</DialogHeader>

				<DialogContent>
					<Form fullForm={form} variant={download.protection?.settings?.formVariant} metadata={{ downloadId: download.id }} />
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<a
			href={getRelationshipObject(download.file)?.url ?? undefined}
			target="_blank"
			rel="noopener noreferrer"
			download
			aria-label="Download"
			className="block"
			title={`Download ${download.title}`}
		>
			{card}
		</a>
	);
};
